import { useMemo, type ComponentType, type SVGProps } from 'react';
import CO2Icon from 'assets/icons/co2-calculator/co2.svg?react';
import LightningIcon from 'assets/icons/lightning-2.svg?react';
import EuroIcon from 'assets/icons/sidebar/euro.svg?react';
import { ArrowRight } from 'assets/icons';
import { clamp, get, round } from 'lodash';
import { formatValue } from 'utils/helpers';
import theme from 'config/theme';
import type { TCO2CalculationResult, TCO2Calculations, TFormatMessage } from '../types';
import s from './EnergyReductionItems.module.scss';

/** The calculation compared with the current one: the potential or the one after the renovations. */
type TEnergyReductionType = 'potential' | 'after';

type TIcon = ComponentType<SVGProps<SVGSVGElement> & { title?: string }>;

interface IEnergyItem {
  icon: TIcon;
  altText: string;
  color: string;
  label: string;
  valueLabel: string;
  actualValue: number;
  potentialValue: number | undefined;
}

interface IEnergyReductionItemProps extends Omit<IEnergyItem, 'icon'> {
  t: TFormatMessage;
  icon: TIcon;
  percentageDiff: number;
}

interface IEnergyReductionItemsProps {
  co2Calculations: TCO2Calculations;
  t: TFormatMessage;
  type?: TEnergyReductionType;
}

// lodash `get` with the default for a missing calculation (`co2_emissions_after` is nullable).
const comparedResultOf: Record<
  TEnergyReductionType,
  (calculations: TCO2Calculations) => Partial<TCO2CalculationResult>
> = {
  potential: (calculations) => get(calculations, 'co2_emissions_potential.result', {}),
  after: (calculations) => get(calculations, 'co2_emissions_after.result', {}),
};

function getEnergyItems(calculations: TCO2Calculations, type: TEnergyReductionType): IEnergyItem[] {
  const potentialValue = comparedResultOf[type](calculations);
  const {
    co2_emissions_before: { result: co2BeforeResult },
  } = calculations;

  return [
    {
      icon: CO2Icon,
      altText: 'alt_text.renovation_house.co2_icon',
      color: theme.successColor,
      label: 'renovation_house_wizard.potential_energy_balance.ghg_balance.label',
      valueLabel: 'renovation_house_wizard.potential_energy_balance.ghg_balance.value',
      actualValue: co2BeforeResult.energy_ghg_balance_per_year,
      potentialValue: potentialValue.energy_ghg_balance_per_year,
    },
    {
      icon: EuroIcon,
      altText: 'alt_text.renovation_house.euro_icon',
      color: theme.primaryColor,
      label: 'renovation_house_wizard.potential_energy_balance.total_energy_costs.label',
      valueLabel: 'renovation_house_wizard.potential_energy_balance.total_energy_costs.value',
      actualValue: co2BeforeResult.total_energy_costs,
      potentialValue: potentialValue.total_energy_costs,
    },
    {
      icon: LightningIcon,
      altText: 'alt_text.renovation_house.lightning_icon',
      color: theme.pastelGreenColor,
      label: 'renovation_house_wizard.potential_energy_balance.total_energy_balance_per_year.label',
      valueLabel:
        'renovation_house_wizard.potential_energy_balance.total_energy_balance_per_year.value',
      actualValue: co2BeforeResult.total_energy_balance_per_year,
      potentialValue: potentialValue.total_energy_balance_per_year,
    },
  ];
}

const EnergyReductionItem = ({
  t,
  label,
  icon: Icon,
  altText,
  color,
  actualValue,
  potentialValue,
  valueLabel,
  percentageDiff,
}: IEnergyReductionItemProps) => {
  const lineWidth = clamp(100 - percentageDiff, 0, 100);
  // Number(): the subtraction coerces a missing potential value like before (undefined -> NaN).
  const valuesDiff = round(actualValue - Number(potentialValue));
  // Bug: a reduction is shown as `100 - reduction` instead of the reduction itself.
  const formattedDiff =
    valuesDiff < 0 ? `+${formatValue(Math.abs(valuesDiff))}` : formatValue(100 - valuesDiff);
  const percentageValue =
    percentageDiff > 100 ? `+${percentageDiff - 100}` : `-${100 - percentageDiff}`;

  return (
    <div className={s.potentialItem}>
      <div className={s.circle} style={{ backgroundColor: color }}>
        <Icon title={t({ id: altText })} width="100%" height="100%" />
      </div>

      <div className={s.info}>
        <p className={s.label}>{t({ id: label })}</p>
        <div className={s.differenceValue}>
          <p className={s.value} style={{ color }}>
            {t({ id: valueLabel }, { value: formattedDiff })}
          </p>
          <div className={s.beforeAndAfterValues}>
            <span style={{ color }}>{formatValue(round(actualValue))}</span>
            <span>
              <ArrowRight />
            </span>
            <span className="text-gray">{formatValue(round(Number(potentialValue)))}</span>{' '}
            <span>({percentageValue}%)</span>
          </div>
        </div>
        <div className={s.barChart}>
          <div
            style={{ backgroundColor: color, width: `${lineWidth}%` }}
            className={s.potentialValue}
          />
        </div>
      </div>
    </div>
  );
};

export default function EnergyReductionItems({
  co2Calculations,
  t,
  type = 'potential',
}: IEnergyReductionItemsProps) {
  const potentialItems = useMemo(
    () => getEnergyItems(co2Calculations, type),
    [co2Calculations, type],
  );
  return (
    <div className={s.potentialValues}>
      {potentialItems.map(({ actualValue, potentialValue, ...rest }) => {
        const percentageDiff = round((Number(potentialValue) / actualValue) * 100);
        return (
          <EnergyReductionItem
            key={rest.label}
            {...{ percentageDiff, potentialValue, actualValue, t, ...rest }}
          />
        );
      })}
    </div>
  );
}
